// Repairs a numeric token that arrived with a stray space after its decimal
// point: "$52. 3M", "$642. 9M", "18. 0%", "$3. 098B".
//
// The stream normalizer inserts a sentence space after a delta ending in "."
// when the next delta starts alphanumeric; a decimal split across deltas is
// caught by that rule. The repair is done here, in the renderer, rather than
// in the backend normalizer: the backend sees two deltas and cannot tell
// "$52." + "3M" from "option 2." + "3 remain.", whereas the rendered text
// carries the context that proves it is one number.
//
// Only two shapes are repaired, both unambiguous:
//   (a) a currency amount: `$` + digits + ". " + digit          -> "$52.3M"
//   (b) digits + ". " + digits immediately followed by a unit    -> "18.0%"
//       (% M K B bn x), where the unit is not followed by a letter/digit
// Everything else is left alone: "2. 3 remain", ordered-list markers
// ("2. Next", "1. 5% growth" at line start), versions, IPs, fenced code and
// inline code. Safe mid-stream: (a) needs no lookahead, (b) requires the unit
// to have arrived.

import { closesFence, mapOutsideInlineCode, openingFence, type FenceMarker } from './markdownCodeGuards';

const CURRENCY_SPLIT = /(\$\d[\d,]*)\. (?=\d)/g;
const UNIT_SPLIT = /(\d+)\. (\d+)(?=%|(?:bn|[MKBx])(?![A-Za-z0-9]))/g;

function repairLine(text: string): string {
  if (!text.includes('. ')) return text;
  const currencyFixed = text.replace(CURRENCY_SPLIT, '$1.');
  return currencyFixed.replace(UNIT_SPLIT, (match, whole: string, fraction: string, offset: number, source: string) => {
    // "1. 5% growth" at the start of a line is an ordered-list marker, not a
    // split decimal; leave it for the Markdown parser.
    if (source.slice(0, offset).trim() === '') return match;
    return `${whole}.${fraction}`;
  });
}

export function normalizeSplitDecimals(markdown: string): string {
  if (!markdown.includes('. ')) return markdown;
  const lines = markdown.split('\n');
  let fence: FenceMarker | null = null;
  let changed = false;
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    if (fence) {
      if (closesFence(line, fence)) fence = null;
      continue;
    }
    const opened = openingFence(line);
    if (opened) {
      fence = opened;
      continue;
    }
    const repaired = mapOutsideInlineCode(line, repairLine);
    if (repaired !== line) {
      lines[index] = repaired;
      changed = true;
    }
  }
  return changed ? lines.join('\n') : markdown;
}
